/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Grid } from '@mui/material';
import React from 'react';
import { CustomIconButton } from '@opengeoweb/shared';
import { Link, LinkOff } from '@opengeoweb/theme';
import type { Dimension } from '@opengeoweb/webmap';
import { webmapUtils } from '@opengeoweb/webmap';
import DimensionSelectSlider from './DimensionSelectSlider';
import type { DimensionConfig } from './utils';
import { dimensionConfig, marksByDimension } from './utils';
import { useWebmapReactTranslation } from '../../utils/i18n';

const Icon: React.FC<{ isLayerDimensionSynced: boolean }> = ({
  isLayerDimensionSynced,
}) =>
  isLayerDimensionSynced ? (
    <Link data-testid="syncIcon" />
  ) : (
    <LinkOff data-testid="syncDisIcon" />
  );

export interface MapDimensionSelectProps {
  layerId: string;
  dimensionName: string;
  handleDimensionValueChanged: (
    layerId: string,
    dimensionName: string,
    dimensionValue: string,
  ) => void;
  handleSyncChanged: (
    layerId: string,
    dimensionName: string,
    dimensionValue: string,
    synced: boolean,
  ) => void;
  layerDimension: Dimension;
  layerName: string;
}

const MapDimensionSelect: React.FC<MapDimensionSelectProps> = ({
  layerId,
  dimensionName,
  handleDimensionValueChanged,
  handleSyncChanged,
  layerDimension,
  layerName,
}: MapDimensionSelectProps) => {
  const { t } = useWebmapReactTranslation();
  const wmLayer = webmapUtils.getWMLayerById(layerId);

  const [layerTitle, setLayerTitle] = React.useState(layerName);

  React.useEffect(() => {
    // if the layername changes, the title needs to be updated
    const updateTitle = async (): Promise<void> => {
      // make sure we have the latest title
      await wmLayer.parseLayer();
      setLayerTitle(wmLayer.title || layerName);
    };
    if (wmLayer) {
      void updateTitle();
    }
  }, [layerName, wmLayer]);

  const dimConfig: DimensionConfig = dimensionConfig(t).find(
    (cnf) => cnf.name === dimensionName,
  )!;

  if (!layerDimension || !wmLayer) {
    return null;
  }
  const wmsDimension = wmLayer.getDimension(dimensionName);

  if (!wmsDimension) {
    return null;
  }

  const marks = marksByDimension(t, wmsDimension);
  const isLayerDimensionSynced =
    layerDimension.synced !== undefined && layerDimension.synced;

  const tooltipTitle = isLayerDimensionSynced
    ? t('webmap-react-click-disconnect')
    : t('webmap-react-click-connect');

  return (
    <Grid key={layerId} size={{ xs: 'auto' }}>
      <CustomIconButton
        data-testid="syncButton"
        onClick={(): void =>
          handleSyncChanged(
            layerId,
            dimensionName,
            layerDimension.currentValue,
            !layerDimension.synced,
          )
        }
        sx={{ margin: '5px' }}
        tooltipTitle={tooltipTitle}
      >
        <Icon isLayerDimensionSynced={isLayerDimensionSynced} />
      </CustomIconButton>

      <DimensionSelectSlider
        isDisabled={false}
        layerTitle={layerTitle}
        managedValue={
          Number(layerDimension.currentValue) || layerDimension.currentValue
        }
        marks={marks}
        onChangeDimensionValue={(value): void => {
          handleDimensionValueChanged(layerId, dimensionName, value);
        }}
        reverse={dimConfig ? dimConfig.reversed : false}
        validSelection={
          layerDimension.synced === false ||
          !(
            layerDimension.validSyncSelection !== undefined &&
            !layerDimension.validSyncSelection
          )
        }
      />
    </Grid>
  );
};

export default MapDimensionSelect;
